import { IconCheck } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import Select, { StylesConfig } from "react-select";
import useGetAllRoomType from "../../../services/room-type/hooks/useGetAllRoomType";

interface RoomTypeFilterProps {
    setRoomTypeFilter: (roomTypeFilter: string[]) => void;
    menuZIndex?: number;
    roomTypeQuery?: any;
}

const RoomTypeFilter: React.FC<RoomTypeFilterProps> = ({ setRoomTypeFilter, menuZIndex, roomTypeQuery }) => {
    const defaultRoomType = { value: "0", label: "All Room Types" };
    const [roomType, setRoomType] = useState<OptionType[]>([defaultRoomType]);
    
    const fallbackQuery = useGetAllRoomType({ all: true });
    const activeQuery = roomTypeQuery || fallbackQuery;
    const { data: roomTypeData, isSuccess, isError, isFetching } = activeQuery;
    const [selectedRooms, setSelectedRooms] = useState<OptionType[]>([defaultRoomType]);

    useEffect(() => {
        if (roomTypeData) {
            setRoomType([
                defaultRoomType,
                ...roomTypeData.data.map((roomType: any) => ({
                    value: roomType.id,
                    label: roomType.name,
                })),
            ]);
        }
    }, [roomTypeData]);

    useEffect(() => {
        if (
            roomTypeData &&
            selectedRooms.length === 1 &&
            selectedRooms[0].value === defaultRoomType.value
        ) {
            setRoomTypeFilter(
                roomTypeData.data
                    .filter((room: any) => room.id !== defaultRoomType.value)
                    .map((room: any) => room.id)
            );
        }
    }, [roomTypeData, selectedRooms]);


    return (
        <RoomSelect
            defaultRoomType={defaultRoomType}
            roomType={roomType}
            isFetching={isFetching}
            isSuccess={isSuccess}
            isError={isError}
            selectedRooms={selectedRooms}
            setSelectedRooms={setSelectedRooms}
            setRoomTypeFilter={setRoomTypeFilter}
            menuZIndex={menuZIndex}
        />
    );
};

export default RoomTypeFilter;

interface OptionType {
    value: string;
    label: string;
}

interface RoomSelectProps {
    defaultRoomType: OptionType;
    roomType: OptionType[];
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    selectedRooms: OptionType[];
    setSelectedRooms: (rooms: OptionType[]) => void;
    setRoomTypeFilter?: (roomTypeFilter: string[]) => void;
    menuZIndex?: number;
}

const CustomOption = (props: any) => {
    const { data, isSelected, innerRef, innerProps } = props;

    return (
        <div
            ref={innerRef}
            {...innerProps}
            className="d-flex justify-content-between align-items-center p-2"
        >
            <span>{data.label}</span>
            {isSelected && <IconCheck size={16} />}
        </div>
    );
};

const RoomSelect: React.FC<RoomSelectProps> = ({
    defaultRoomType,
    roomType,
    isFetching,
    isSuccess,
    isError,
    selectedRooms,
    setSelectedRooms,
    setRoomTypeFilter,
    menuZIndex = 1000,
}) => {
    const customStyles: StylesConfig<OptionType, true> = {
        menuPortal: (base) => ({
            ...base,
            zIndex: menuZIndex, // Pastikan z-index cukup tinggi
        }),
    };

    const handleChange = (selected: OptionType[]) => {
        // Jika yang dipilih adalah defaultRoomType, maka tidak bisa memilih lebih dari 1
        if (selected.length === 1 && selected[0].value === defaultRoomType.value) {
            setSelectedRooms([defaultRoomType]);
            setRoomTypeFilter?.(roomType.filter((room) => room.value !== defaultRoomType.value).map((room) => room.value));
        } else {
            // Jika memilih opsi lain, defaultRoomType akan dihapus
            setSelectedRooms(selected.filter((room) => room.value !== defaultRoomType.value));
            setRoomTypeFilter?.(selected.map((room) => room.value));
        }
    };

    return (
        <Select
            value={selectedRooms}
            options={roomType}
            className="p-0"
            styles={{
                ...customStyles,
                singleValue: (base) => ({
                    ...base,
                    fontWeight: "bold",
                }),
            }}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            isLoading={isFetching}
            isDisabled={!isSuccess || isError}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={(selected) => handleChange(selected as OptionType[])}
            components={{
                Option: CustomOption,
                MultiValue: () => null,
                ValueContainer: ({ getValue }) => {
                    const selected = getValue();
                    return (
                        <div className="p-2">
                            {selected.length === 1 && selected[0].value === defaultRoomType.value
                                ? defaultRoomType.label
                                : `${selected.length} Room Selected`}
                        </div>
                    );
                },
            }}
        />
    );
};
